import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { http } from '../api/http';

/**
 * `/api/archivos/:id` exige la cabecera de autorizacion, y un `<img>` o un
 * enlace no la envian: con la ruta en `src` la imagen sale rota (401). Se pide
 * con el cliente autenticado y se muestra desde un blob local. No se abre el
 * endpoint sin sesion: una imagen clinica sin sesion es una fuga.
 */
function rutaDeApi(url: string): string {
  // El backend devuelve la ruta con el prefijo `/api`; el cliente ya lo pone.
  return url.startsWith('/api/') ? url.slice(4) : url;
}

export async function blobDeArchivo(url: string): Promise<Blob> {
  const { data } = await http.get<Blob>(rutaDeApi(url), { responseType: 'blob' });
  return data;
}

interface Props {
  url: string;
  alt: string;
  sx?: SxProps<Theme>;
}

export function ImagenProtegida({ url, alt, sx }: Props) {
  const [origen, setOrigen] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    let vigente = true;
    let enlace: string | null = null;
    setOrigen(null);
    setFallo(false);
    blobDeArchivo(url)
      .then((blob) => {
        if (!vigente) return;
        enlace = URL.createObjectURL(blob);
        setOrigen(enlace);
      })
      .catch(() => vigente && setFallo(true));
    return () => {
      vigente = false;
      if (enlace) URL.revokeObjectURL(enlace);
    };
  }, [url]);

  if (!origen) {
    // Mismo hueco que ocupara la imagen, para que la cuadricula no salte.
    return (
      <Box
        role="img"
        aria-label={fallo ? `${alt} (no se pudo cargar)` : alt}
        aria-busy={!fallo}
        sx={[
          {
            bgcolor: 'var(--bg-sunken)',
            color: 'var(--text-secondary)',
            display: 'grid',
            placeItems: 'center',
            fontSize: 12,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {fallo ? 'Sin vista previa' : null}
      </Box>
    );
  }

  return <Box component="img" src={origen} alt={alt} sx={sx} />;
}

/**
 * Abre un archivo protegido a tamano completo en otra pestana. La ventana se
 * abre ANTES de esperar la descarga: abrirla despues de un `await` ya no cuenta
 * como gesto del usuario y el navegador la bloquea. Falso si la bloqueo.
 */
export async function abrirArchivoProtegido(url: string): Promise<boolean> {
  const ventana = window.open('', '_blank');
  if (!ventana) return false;
  ventana.opener = null;
  try {
    const enlace = URL.createObjectURL(await blobDeArchivo(url));
    ventana.location.href = enlace;
    setTimeout(() => URL.revokeObjectURL(enlace), 60_000);
    return true;
  } catch (error) {
    ventana.close();
    throw error;
  }
}
